import { darken } from '@osmosis/shared/styles/colorModifiers';
import EditablePropsType from '../../../types/composer';
import {
  bg0,
  bgPrimaryLightest,
  borderColorBlue700,
  borderColorGray200,
} from '@osmosis/tailwind-ui/styles';

export const textColorMap: { [index: string]: string } = {
  white: 'text',
  transparent: 'text',
  '': 'text',
  'bg-1': 'text',
  primaryLightest: 'primaryText',
  lightestBlue: 'textLightestBlue',
  transparentWhiteText: 'transparentWhiteText',
};

/**
 * Get the converted legacy text color.
 */
export const getTextColor = (theme: string): string => {
  return textColorMap[theme] || 'textInvert';
};

/**
 * Get the converted legacy button color.
 */
export const getButtonColor = (
  theme: string,
  colors: { [index: string]: string },
): string => {
  return colors[theme] ? darken(0.15, colors[theme]) : colors.gray10;
};

export const themeSchemaOptions = [
  {
    value: 'transparent',
    label: 'Transparent',
  },
  {
    value: 'transparentWhiteText',
    label: 'Transparent white text',
  },
  {
    value: 'bg-1',
    label: 'Light Gray',
  },
  {
    value: 'primaryText',
    label: 'Blue',
  },
  {
    value: 'primaryLightest',
    label: 'Light Blue',
  },
  {
    value: 'lightestBlue',
    label: 'Lightest Blue',
  },
  {
    value: 'teal',
    label: 'Teal',
  },
  {
    value: 'olive',
    label: 'Olive',
  },
  {
    value: 'green',
    label: 'Green',
  },
  {
    value: 'orange',
    label: 'Orange',
  },
  {
    value: 'red',
    label: 'Red',
  },
  {
    value: 'pink',
    label: 'Pink',
  },
  {
    value: 'lavender',
    label: 'Lavender',
  },
  {
    value: 'white',
    label: 'White',
  },
  {
    value: 'legacy',
    label: 'Legacy',
  },
];

export const opacityOptions = [
  { label: '', value: '' },
  { label: '20%', value: '0.2' },
  { label: '40%', value: '0.4' },
  { label: '60%', value: '0.6' },
  { label: '70%', value: '0.7' },
];

export const opacitySchema = {
  label: 'Opacity',
  name: 'opacity',
  type: EditablePropsType.Select,
  selectOptions: {
    options: opacityOptions,
  },
};

export const themeToTailwindClassMap: { [index: string]: string } = {
  transparent: bg0,
  white: bg0,
  'bg-1': '',
  primaryText: 'bg-blue-700',
  bgLightestBlue: 'bg-light-blue-500',
  primaryLightest: `${bgPrimaryLightest}`,
  teal: 'bg-teal-600',
  olive: 'bg-olive-600',
  green: 'bg-green-600',
  orange: 'bg-orange-600',
  red: 'bg-red-600',
  pink: 'bg-pink-600',
  lavender: 'bg-lavender-600',
  legacy: 'bg-blue-600',
};

/**
 * Get the tailwind text color for the theme.
 */
export const getTailwindTextColorForTheme = (theme: string): string => {
  switch (theme) {
    case 'transparent':
    case 'bg-1':
    case 'primaryLightest':
    case 'lightestBlue':
    case 'white':
      return 'text-black';
    default:
      return 'text-white';
  }
};

/**
 * Get the tailwind border color for the theme.
 * @param theme The theme to get the border color for.
 * @returns Border color class.
 */
export const getTailwindBorderColorForTheme = (theme: string): string => {
  switch (theme) {
    case 'transparent':
      return borderColorGray200;
    case 'bg-1':
      return '';
    case 'primaryLightest':
      return borderColorBlue700;
    default:
      return '';
  }
};

const themeSchema = {
  label: 'Theme',
  name: 'theme',
  type: EditablePropsType.Select,
  selectOptions: {
    options: themeSchemaOptions,
  },
};

export default themeSchema;
